import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Button,
  ComposedModal,
  InlineLoading,
  ModalBody,
  ModalFooter,
  ModalHeader,
  StructuredListBody,
  StructuredListCell,
  StructuredListRow,
  StructuredListWrapper,
} from "@carbon/react";
import { UserResponse } from "../../../types";

type PotencialSupplierProps = {
  open: boolean;
  materialId: string;
  potencialSupplier: {
    user: UserResponse;
    invited: boolean;
  };
  setOpenSupplier: (value: boolean) => void;
  isLoadingMaterialOffer: boolean;
  onCreateMaterialOfferInvitation: (data: any) => void;
};

/**
 * A potential supplier's detail — a company in the material's country that has not yet been invited.
 * Rebuilt on Carbon as a self-contained `ComposedModal` (replacing the pre-Carbon `<Slide>` +
 * Tailwind card): provider info as a `StructuredList`, an invite `Button` in the footer that opens a
 * Carbon confirmation `ComposedModal`. The invite mutation is unchanged.
 */
const PotencialSupplier = ({
  open,
  materialId,
  potencialSupplier: { user, invited },
  setOpenSupplier,
  isLoadingMaterialOffer,
  onCreateMaterialOfferInvitation,
}: PotencialSupplierProps) => {
  const { t } = useTranslation();
  const [openConfirm, setOpenConfirm] = useState(false);

  const handleSupplierInvitation = () => {
    if (invited) return;
    onCreateMaterialOfferInvitation({
      user: { id: user?.id },
      material: { id: materialId },
    });
    setOpenConfirm(false);
    setOpenSupplier(false);
  };

  const infoRow = (label: string, value?: string) => (
    <StructuredListRow>
      <StructuredListCell head>{label}</StructuredListCell>
      <StructuredListCell>{value || "-"}</StructuredListCell>
    </StructuredListRow>
  );

  return (
    <>
      <ComposedModal
        open={open}
        onClose={() => setOpenSupplier(false)}
        size="md"
      >
        <ModalHeader label={t("offer.title")} title={user?.company?.name} />
        <ModalBody>
          {isLoadingMaterialOffer ? (
            <InlineLoading description={t("cta.loading")} />
          ) : (
            <StructuredListWrapper isCondensed ariaLabel={t("offer.information")}>
              <StructuredListBody>
                {infoRow(t("offer.taxId"), user?.company?.taxId)}
                {infoRow(t("offer.seller"), user?.fullName)}
                {infoRow(t("offer.email"), user?.email)}
                {infoRow(t("offer.phone"), user?.phone)}
              </StructuredListBody>
            </StructuredListWrapper>
          )}
        </ModalBody>
        {!invited && !isLoadingMaterialOffer && (
          <ModalFooter>
            <Button onClick={() => setOpenConfirm(true)}>
              {t("material.inviteSupplier")}
            </Button>
          </ModalFooter>
        )}
      </ComposedModal>

      {/* Rendered after the main modal so it stacks on top (equal z-index → DOM order wins). */}
      <ComposedModal
        open={openConfirm}
        onClose={() => setOpenConfirm(false)}
        size="sm"
      >
        <ModalHeader title={`${t("invite.supplier")} ${user?.company?.name}`} />
        <ModalBody>
          <p>{t("invite.supplierQuestion")}</p>
        </ModalBody>
        <ModalFooter>
          <Button kind="secondary" onClick={() => setOpenConfirm(false)}>
            {t("cta.cancel")}
          </Button>
          <Button onClick={handleSupplierInvitation}>{t("cta.add")}</Button>
        </ModalFooter>
      </ComposedModal>
    </>
  );
};

export default PotencialSupplier;
